/* eslint no-console: 0 */
import { config } from 'config';
import { User } from 'types';
import { getCookie, setCookie } from 'utils';

export type PageTrackingInfo = {
  name: string;
  nameError?: string;
  level2?: string;
  chapter1?: string;
  chapter2?: string;
  chapter3?: string;
  customVars?: {
    '30'?: string;
    '38'?: string;
    '41'?: string;
  };
};

export type ClickTrackingInfo = PageTrackingInfo & { elem?: HTMLElement; type: string };

type UserInfo = {
  id: string;
  category?: string;
};

type CustomVars = {
  customVars?: {
    site?: { [k: string]: string | undefined };
    page?: { [k: string]: string | undefined };
  };
};

type Tag = {
  dispatch: () => void;
  page: {
    set: (info: PageTrackingInfo) => void;
    send: (info: PageTrackingInfo & CustomVars) => void;
  };
  click: {
    send: (info: ClickTrackingInfo) => void;
  };
  identifiedVisitor: {
    set: (info: UserInfo) => void;
    unset: () => void;
  };
  privacy: {
    setVisitorMode: (a: string, b: string) => void | undefined;
    setVisitorOptout: () => void | undefined;
  };
};

class ATInternetTrackingService {
  private readonly tag: Tag;
  private user?: string;
  private userType?: string;
  private pageError = false;
  private currentPage?: PageTrackingInfo;
  private customContext?: {
    '30'?: string;
    '38'?: string;
    '41'?: string;
  };

  constructor() {
    this.tag = this.newTag({
      secure: true,
      site: config.AT_TRACKING_ID,
      ignoreEmptyChapterValue: true,
      collectDomainSSL: config.AT_COLLECT_DOMAIN,
      cookieDomain: config.COOKIE_DOMAIN,
    });
    const atPrivacy = getCookie('atPrivacy');
    if (atPrivacy == null || atPrivacy === 'exempt') {
      this.tag.privacy.setVisitorMode('cnil', 'exempt');
      if (atPrivacy == null) {
        const expires = new Date();
        expires.setTime(expires.getTime() + 1000 * 3600 * 24 * 182);
        setCookie('atPrivacy', 'exempt', { expires: expires.toUTCString() });
      }
    } else if (atPrivacy === 'optout') {
      this.tag.privacy.setVisitorOptout();
    }
  }

  newTag(options: any): Tag {
    // @ts-ignore
    if (window.ATInternet) {
      // @ts-ignore
      return new window.ATInternet.Tracker.Tag(options);
    } else {
      return {
        dispatch: () => console.info('Dispatching tracking'),
        page: {
          set: (info: PageTrackingInfo) => console.info('Setting page tracking: ' + JSON.stringify(info, null, 2)),
          send: (info: PageTrackingInfo & CustomVars) =>
            console.info('Sending page tracking: ' + JSON.stringify(info, null, 2)),
        },
        click: {
          send: info => console.info('Tracking click: ' + JSON.stringify(info, null, 2)),
        },
        identifiedVisitor: {
          set: info => console.info('Setting user: ' + JSON.stringify(info, null, 2)),
          unset: () => console.info('Unsetting user'),
        },
        privacy: {
          setVisitorMode: (...args) => console.info('Setting visitor mode: ', args),
          setVisitorOptout: () => console.info('Setting visitor optout'),
        },
      };
    }
  }

  sendPage(info: PageTrackingInfo) {
    this.currentPage = Object.assign({}, info);
    let customVars = info.customVars;
    delete info.customVars;

    if (this.customContext) {
      customVars = Object.assign(customVars, this.customContext);
      this.customContext = undefined;
    }

    if (this.pageError) {
      this.pageError = false;
      info.name = info.nameError || info.name;
    }

    this.tag.page.send({ ...info, customVars: { site: customVars } });
  }

  sendClick(info: ClickTrackingInfo) {
    this.tag.click.send(info);
  }

  setContext(context: { '38'?: string; '41'?: string }) {
    this.customContext = context;
  }

  setPageError() {
    this.pageError = true;
  }

  sendCurrentPageError(context?: { '41'?: string }) {
    if (this.currentPage) {
      this.setContext({ ...context, '38': '[' + this.currentPage.nameError + ']' });
      this.setPageError();
      this.sendPage(this.currentPage);
    }
  }

  setUser(user: User) {
    if (!user.isPro) {
      this.user = user.userCorrelationId;
      this.userType = user.isCollaborator ? '3' : '2';
    } else {
      this.user = user.username;
      this.userType = '1';
    }
    this.tag.identifiedVisitor.set({
      id: this.user,
      category: this.userType,
    });
  }

  unsetUser() {
    this.user = undefined;
    this.userType = undefined;
    this.tag.identifiedVisitor.unset();
  }
}

export const Tracking = new ATInternetTrackingService();
